import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { App } from "./App.tsx";
import { AnalyticsProvider } from "./AnalyticsProvider.tsx";
import { KindeProvider } from "@kinde-oss/kinde-auth-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { CookieConsentBanner } from "@/features/cookie-consent";
import { initAnalytics } from "@/shared/lib/analytics";
import "../i18n";

import "./index.css";

const isProd = import.meta.env.MODE === "production";

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN, // Берем из переменных окружения

  // Отключаем Sentry полностью, если мы в режиме разработки
  enabled: import.meta.env.PROD,

  environment: import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE,

  integrations: [Sentry.browserTracingIntegration()],

  // Настройка трейсинга
  tracePropagationTargets: [
    "localhost",
    /^\//, // Разрешает все относительные пути (напр. /api/users)

    // Динамически добавляем текущий домен
    // Это разрешит отправку хедеров, если ты делаешь запросы вида:
    // fetch('https://my-preview-branch.pages.dev/api/...')
    window.location.origin,
  ],

  // Performance Monitoring
  tracesSampleRate: isProd ? 0.1 : 1.0,

  // Session Replay
  replaysSessionSampleRate: 0, // Записывать 10% всех сессий
  replaysOnErrorSampleRate: 1.0, // Записывать 100% сессий, где случилась ошибка

  // Убираем шум (опционально)
  ignoreErrors: [
    "ResizeObserver loop limit exceeded",
    "ResizeObserver loop completed with undelivered notifications", // Тоже частая ошибка chrome
  ],

  // Enable logs to be sent to Sentry
  enableLogs: true,

  // Очистка данных Kinde перед отправкой (чтобы токены не улетели)
  beforeSend(event) {
    if (event.request?.headers) {
      delete event.request.headers["Authorization"];
      // Можно удалить и куки, если там есть чувствительные данные
      // delete event.request.headers['Cookie'];
    }
    return event;
  },
});

if (isProd) {
  void Sentry.lazyLoadIntegration("replayIntegration").then((replayIntegration) => {
    Sentry.addIntegration(replayIntegration());
  });
}

initAnalytics();

const root = createRoot(document.getElementById("root")!);

const queryClient = new QueryClient();

root.render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <KindeProvider
          domain={import.meta.env.VITE_KINDE_DOMAIN}
          clientId={import.meta.env.VITE_KINDE_CLIENT_ID}
          redirectUri={window.location.origin + "/ask"}
          logoutUri={import.meta.env.VITE_KINDE_LOGOUT_URL}
          refreshOnFocus
        >
          <AnalyticsProvider>
            <App />
            <CookieConsentBanner />
          </AnalyticsProvider>
        </KindeProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>
);
